/**
 * Admin 测试中心 —— 触发各种 cron / 事件，便于即时验证（避免等明天）
 * 只 admin 可见。路由 /admin/test-center
 */
import { useState } from 'react';
import { Input, message } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { PageHeader, HomeCard, Btn, Pill } from '../../components/ui';
import { fmtTime } from '../../lib/format';

interface ActionResult {
  ts: number;
  label: string;
  ok: boolean;
  payload: any;
}

export function AdminTestCenterPage() {
  const [history, setHistory] = useState<ActionResult[]>([]);
  const [orderId, setOrderId] = useState('');

  const push = (label: string, ok: boolean, payload: any) => {
    setHistory((h) => [{ ts: Date.now(), label, ok, payload }, ...h].slice(0, 20));
  };

  const mkAction = (label: string, url: string) => useMutation({
    mutationFn: () => $.post(url, {}),
    onSuccess: (r) => { message.success(`${label} 已触发`); push(label, true, r); },
    onError: (e: any) => { message.error(`${label} 失败：${e?.message ?? ''}`); push(label, false, e?.message); },
  });

  const triggerAlerts = mkAction('立刻跑团队预警 cron', '/admin/test/trigger-daily-alert');
  const triggerBirthday = mkAction('立刻跑生日提醒 cron', '/admin/test/trigger-birthday-check');
  const recomputePriority = mkAction('重算所有客户 priority', '/admin/test/recompute-priority');
  const recomputeHealth = mkAction('重算所有客户 health', '/admin/test/recompute-health');
  const genDailyReports = mkAction('生成所有 sales 日报', '/admin/test/generate-daily-reports');
  const recomputeSalesPerf = mkAction('重算销售员表现指标 (P3-1)', '/admin/test/recompute-sales-performance');
  const mockXhsLeads = mkAction('模拟 5 条小红书线索 (P3-1)', '/admin/test/mock-xhs-leads');
  // P3-2
  const recomputeHealthAlerts = mkAction('重算客户健康度告警 (P3-2)', '/admin/test/recompute-customer-health');
  const markLongCycle = mkAction('标记长周期客户 (P3-2)', '/admin/test/mark-long-cycle');
  const forcePool = mkAction('强制 pool_force 客户进公海 (P3-2)', '/admin/test/force-unreachable-to-pool');
  const createQuotas = mkAction('创建今日跟进配额 (P3-2)', '/admin/test/create-daily-quotas');
  const quotaSummary = mkAction('运行配额晚间汇总 (P3-2)', '/admin/test/quota-evening-summary');
  const generateRevival = mkAction('生成 AI 挽回建议 (P3-2)', '/admin/test/generate-revival-suggestions');
  // P3-3 / P3-4
  const runReconcile = mkAction('运行昨日对账 (P3-3)', '/admin/test/run-daily-reconcile');
  const sendReconcileDt = mkAction('推送对账钉钉 (P3-3)', '/admin/test/send-reconcile-dingtalk');
  const recomputeMonthly = mkAction('重算月度员工绩效 (P3-4)', '/admin/test/recompute-monthly-performance');
  const backfillQuality = mkAction('AI 跟进质量回填最近 100 条 (P3-4)', '/admin/test/backfill-followup-quality');
  // P3-5
  const triggerInvoiceReminders = mkAction('发票长尾提醒 (P3-5)', '/admin/test/trigger-invoice-daily-reminders');
  const triggerInvoiceWeekly = mkAction('发票 AI 周报 (P3-5)', '/admin/test/trigger-invoice-weekly-summary');
  const mockInvoiceFlow = mkAction('模拟发票全流程 (P3-5)', '/admin/test/mock-invoice-full-flow');

  const triggerOrder = useMutation({
    mutationFn: () => $.post(`/admin/test/trigger-order-approved/${orderId}`, {}),
    onSuccess: (r) => { message.success('order.approved 已触发'); push(`order.approved #${orderId}`, true, r); },
    onError: (e: any) => { message.error(`触发失败：${e?.message ?? ''}`); push(`order.approved #${orderId}`, false, e?.message); },
  });

  const Card = ({
    title, desc, action, btn, danger,
  }: { title: string; desc: string; action: any; btn: string; danger?: boolean }) => (
    <div style={{
      padding: 16, background: 'var(--bg-1)', border: '1px solid var(--border)',
      borderRadius: 8, marginBottom: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>{title}</div>
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{desc}</div>
        </div>
        <Btn
          kind={danger ? 'default' : 'primary'}
          onClick={() => action.mutate()}
          disabled={action.isPending}
        >
          {action.isPending ? '执行中…' : btn}
        </Btn>
      </div>
    </div>
  );

  return (
    <>
      <PageHeader
        breadcrumb={['Admin', '测试中心']}
        title="🧪 测试中心"
        subtitle="手动触发 cron / 事件 —— 验证流程不用等明天"
      />
      <div className="work-content">
        <HomeCard title="🔔 通知 & 提醒">
          <div style={{ padding: 16 }}>
            <Card
              title="立刻跑团队预警 cron"
              desc="即跑 morningAlertsDingtalk —— 给所有 manager/director 推今日异常告警。mock 模式下只 log。"
              action={triggerAlerts}
              btn="执行"
            />
            <Card
              title="立刻跑生日 / 加微信周年 cron"
              desc="即跑 checkBirthdayReminders —— 扫今天生日 + 加微信 N 周年的客户，推 owner 钉钉。"
              action={triggerBirthday}
              btn="执行"
            />
          </div>
        </HomeCard>

        <HomeCard title="🎉 成交贺报">
          <div style={{ padding: 16 }}>
            <div style={{ padding: 16, background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 8 }}>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>mock 触发 order.approved 事件</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 10 }}>
                不真的修改订单状态，只对指定订单 ID emit 一次 order.approved，
                走完贺报全员通知 + 钉钉发送链路。
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Input
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                  placeholder="输入订单 ID (如 1)"
                  style={{ flex: 1 }}
                />
                <Btn
                  kind="primary"
                  onClick={() => triggerOrder.mutate()}
                  disabled={!orderId || triggerOrder.isPending}
                >
                  {triggerOrder.isPending ? '执行中…' : '触发'}
                </Btn>
              </div>
            </div>
          </div>
        </HomeCard>

        <HomeCard title="🧮 重算 / 重建">
          <div style={{ padding: 16 }}>
            <Card
              title="重算所有客户 priority"
              desc="即跑 priority.recomputeBatch —— 3000+ 客户预计 1-3 秒。"
              action={recomputePriority}
              btn="执行"
            />
            <Card
              title="重算所有客户 health"
              desc="即跑 health.recomputeBatch —— 5 档分布刷新。"
              action={recomputeHealth}
              btn="执行"
            />
            <Card
              title="生成所有 sales 的今日日报"
              desc="即跑 dailyReports.generateForAll —— 不用等 21:00 cron。"
              action={genDailyReports}
              btn="执行"
            />
            <Card
              title="重算销售员表现指标 (P3-1)"
              desc="即跑 salesPerf.recomputeAll —— 影响 lead 分配的 composite_score。"
              action={recomputeSalesPerf}
              btn="执行"
            />
          </div>
        </HomeCard>

        <HomeCard title="🎯 投流（P3-1）">
          <div style={{ padding: 16 }}>
            <Card
              title="模拟 5 条小红书线索"
              desc="不走签名（mock），直接 INSERT 5 条 marketing_leads 并触发 AI 分析 + 分配。需先在 /marketing/accounts 创建账号。"
              action={mockXhsLeads}
              btn="一键灌测"
            />
          </div>
        </HomeCard>

        <HomeCard title="🛡️ 客户保护（P3-2）">
          <div style={{ padding: 16 }}>
            <Card
              title="重算客户健康度告警"
              desc="即跑 health-monitor —— 给所有 active 客户算 alert_level + 推送升级告警。"
              action={recomputeHealthAlerts}
              btn="执行"
            />
            <Card
              title="标记长周期客户"
              desc="加微信 ≥ 60 天 + L1-L4 + 有跟进 + 未成交 → is_long_cycle=1。"
              action={markLongCycle}
              btn="执行"
            />
            <Card
              title="强制 pool_force 客户进公海"
              desc="把超期 ≥ pool_force_days 的客户从 owner 名下移到公海。"
              action={forcePool}
              btn="执行"
            />
            <Card
              title="创建今日跟进配额"
              desc="给所有 sales 建当日 daily_followup_quota 行（quota_target=10）。"
              action={createQuotas}
              btn="执行"
            />
            <Card
              title="运行配额晚间汇总"
              desc="即跑 quota.eveningSummary —— 算 met_quota 标志 + 推送主管"
              action={quotaSummary}
              btn="执行"
            />
            <Card
              title="生成 AI 挽回建议"
              desc="给 death/dying 客户生成 customer_revival 建议（24h 内已生成跳过）。"
              action={generateRevival}
              btn="执行"
            />
          </div>
        </HomeCard>

        <HomeCard title="📊 对账 & 绩效（P3-3 / P3-4）">
          <div style={{ padding: 16 }}>
            <Card
              title="运行昨日对账"
              desc="即跑 ReconcileService.runAllAccountsDaily(yesterday) —— 给每个 active 投流账号生成 marketing_daily_reconcile 行。"
              action={runReconcile}
              btn="执行"
            />
            <Card
              title="推送对账钉钉"
              desc="即跑 sendDailyDingtalk(yesterday) —— 把昨日对账明细推 admin/director。"
              action={sendReconcileDt}
              btn="执行"
            />
            <Card
              title="重算月度员工绩效"
              desc="即跑 MonthlyPerformanceCalculator.calculateAll() —— 算当月 6 维综合分 + 排名。"
              action={recomputeMonthly}
              btn="执行"
            />
            <Card
              title="AI 跟进质量回填（最近 100 条）"
              desc="给最近 100 条 follow_up 跑质量评分（已有跳过）—— 适合首次启用 P3-4 时回填历史。"
              action={backfillQuality}
              btn="执行"
            />
          </div>
        </HomeCard>

        <HomeCard title="📋 发票工作流（P3-5）">
          <div style={{ padding: 16 }}>
            <Card
              title="发票长尾提醒"
              desc="即跑 10:00 cron — 已开 3 天未发提醒销售员；已发 7 天未确认追问。"
              action={triggerInvoiceReminders}
              btn="执行"
            />
            <Card
              title="发票 AI 周报"
              desc="即跑 周一 09:00 cron — 给 director-finance 发上周开票汇总 + 本周预测。"
              action={triggerInvoiceWeekly}
              btn="执行"
            />
            <Card
              title="模拟发票全流程（demo）"
              desc="自动跑 request → open → sent → confirmed 4 步，给客户#1 造一条完整 timeline 的演示发票。"
              action={mockInvoiceFlow}
              btn="生成 demo"
            />
          </div>
        </HomeCard>

        {history.length > 0 && (
          <HomeCard title={`📜 最近 ${history.length} 次操作`}>
            <div style={{ padding: 12 }}>
              {history.map((h) => (
                <div key={h.ts} style={{
                  padding: '8px 12px', marginBottom: 6,
                  background: 'var(--bg-2)', borderRadius: 4,
                  borderLeft: `3px solid ${h.ok ? 'var(--success)' : 'var(--danger)'}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Pill tone={h.ok ? 'success' : 'danger'}>{h.ok ? 'OK' : 'FAIL'}</Pill>
                    <span style={{ fontWeight: 500 }}>{h.label}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-3)' }}>
                      {fmtTime(h.ts)}
                    </span>
                  </div>
                  <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-3)', fontFamily: 'monospace' }}>
                    {typeof h.payload === 'object' ? JSON.stringify(h.payload) : String(h.payload)}
                  </div>
                </div>
              ))}
            </div>
          </HomeCard>
        )}
      </div>
    </>
  );
}
